import { useEffect, useState } from 'react';
import { X, Save, AlertTriangle, HelpCircle, Loader2 } from 'lucide-react';
import { elementeApi, wizardApi, Pruefbereich, TechnischesFeld } from '../services/api';
import { FotoPanel } from './FotoPanel';
import { WissensPanel } from './WissensPanel';
import type { ElementWithDetails } from '../types/elemente';

interface GenericElementCaptureWizardProps {
    stockwerkId: number;
    stockwerkName: string;
    bereich: Pruefbereich;
    onClose: () => void;
    /**
     * Bestehendes Element -- schaltet den Wizard in den Bearbeiten-Modus
     * (Phase 6, Lücke 1). Ohne dieses Prop wird ein neues Element
     * angelegt wie bisher.
     */
    element?: ElementWithDetails | null;
}

interface Pruefpunkt {
    id: string;
    text: string;
    pflicht?: boolean;
    grenzwert?: string;
    quelle?: string;
}

interface PruefchecklisteVorlage {
    id: number;
    bezeichnung: string;
    din_norm?: string;
    kategorie: string;
    pruefpunkte: Pruefpunkt[];
}

type Bewertung = 'i.O.' | 'n.i.O.' | 'nicht_geprueft';

/**
 * Generisches Erfassungsformular fuer Pruefbereiche ohne eigenes
 * spezialisiertes Formular (Phase 6, Luecke 2).
 *
 * Baut das Formular ausschliesslich aus Katalogdaten auf:
 * - `bereich.technische_felder` (Deckdaten, z.B. Loeschmittel/Fuellmenge)
 * - den Pruefchecklisten aus `pruefchecklisten_vorlagen`, gefiltert nach
 *   `element_typ` (wiederverwendet ueber die vorhandene
 *   `GET /api/wizard/begehung/checklisten`-Route)
 *
 * Erfindet dabei keine Fachinhalte -- Pruefpunkttexte, Grenzwerte und
 * Quellenangaben kommen unveraendert aus den bereits gesaeten
 * Checklisten (siehe scripts/pruefkatalogPhase6Seeden.ts). Tuer/Schott
 * nutzen weiterhin DoorCaptureWizard.tsx; dieses Formular ist NUR fuer
 * Bereiche ohne eigenes Spezialformular gedacht.
 */
export default function GenericElementCaptureWizard({
    stockwerkId,
    stockwerkName,
    bereich,
    onClose,
    element,
}: GenericElementCaptureWizardProps) {
    const istBearbeiten = !!element?.id;

    const [checklisten, setChecklisten] = useState<PruefchecklisteVorlage[]>([]);
    const [ladeChecklisten, setLadeChecklisten] = useState(true);

    const [elementIdKunde, setElementIdKunde] = useState(element?.element_id_kunde || '');
    const [raum, setRaum] = useState(element?.raum || '');
    const [feldwerte, setFeldwerte] = useState<Record<string, any>>(() => {
        const { _pruefbereich_checkliste, ...felder } = (element?.technische_daten || {}) as Record<string, any>;
        return felder;
    });
    const [bewertungen, setBewertungen] = useState<Record<string, Bewertung>>({});
    const [notizen, setNotizen] = useState<Record<string, string>>({});
    const [speichert, setSpeichert] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    // Nach dem ersten Anlegen (Create) haelt diese Kennung das neue
    // Element fest, damit direkt im Anschluss Fotos ergaenzt werden
    // koennen, statt den Wizard erneut oeffnen zu muessen.
    const [angelegtesElementId, setAngelegtesElementId] = useState<number | null>(null);

    useEffect(() => {
        let aktiv = true;
        setLadeChecklisten(true);
        wizardApi
            .getChecklisten(bereich.schluessel as any)
            .then((data: PruefchecklisteVorlage[]) => {
                if (!aktiv) return;
                const vorlagen = Array.isArray(data) ? data : [];
                setChecklisten(vorlagen);

                // Bearbeiten-Modus: gespeicherte Bewertungen/Notizen aus
                // `technische_daten._pruefbereich_checkliste` je Pruefpunkt-ID
                // in die aktuelle Vorlage zurueckspielen.
                const gespeicherteCheckliste = (element?.technische_daten as any)?._pruefbereich_checkliste;
                if (Array.isArray(gespeicherteCheckliste)) {
                    const neueBewertungen: Record<string, Bewertung> = {};
                    const neueNotizen: Record<string, string> = {};
                    for (const cl of gespeicherteCheckliste) {
                        for (const p of cl.pruefpunkte ?? []) {
                            if (p?.id === undefined) continue;
                            if (p.bewertung) neueBewertungen[p.id] = p.bewertung;
                            if (p.notiz) neueNotizen[p.id] = p.notiz;
                        }
                    }
                    setBewertungen(neueBewertungen);
                    setNotizen(neueNotizen);
                }
            })
            .catch(() => {
                if (aktiv) setChecklisten([]);
            })
            .finally(() => {
                if (aktiv) setLadeChecklisten(false);
            });
        return () => {
            aktiv = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bereich.schluessel]);

    const setFeldwert = (feld: TechnischesFeld, wert: any) => {
        setFeldwerte((prev) => ({ ...prev, [feld.schluessel]: wert }));
    };

    const setBewertung = (pruefpunktId: string, wert: Bewertung) => {
        setBewertungen((prev) => ({ ...prev, [pruefpunktId]: wert }));
    };

    const fehlendePflichtfelder = bereich.technische_felder.filter(
        (f) => f.pflicht && (feldwerte[f.schluessel] === undefined || feldwerte[f.schluessel] === '')
    );
    const fehlendePflichtpunkte = checklisten
        .flatMap((cl) => cl.pruefpunkte)
        .filter((p) => p.pflicht && (bewertungen[p.id] ?? 'nicht_geprueft') === 'nicht_geprueft');

    const kannSpeichern = elementIdKunde.trim().length > 0 && !speichert;

    const handleSave = async () => {
        if (!kannSpeichern) return;
        setSpeichert(true);
        setFehler(null);
        try {
            const alleBewertungen = Object.values(bewertungen);
            const hatMangel = alleBewertungen.includes('n.i.O.');
            const status = hatMangel ? 'n.i.O.' : 'i.O.';

            const checklisteErgebnisse = checklisten.map((cl) => ({
                checkliste_id: cl.id,
                bezeichnung: cl.bezeichnung,
                kategorie: cl.kategorie,
                din_norm: cl.din_norm,
                pruefpunkte: cl.pruefpunkte.map((p) => ({
                    id: p.id,
                    text: p.text,
                    pflicht: p.pflicht,
                    grenzwert: p.grenzwert,
                    bewertung: bewertungen[p.id] ?? 'nicht_geprueft',
                    notiz: notizen[p.id] || undefined,
                })),
            }));

            const technischeDaten: Record<string, any> = {
                ...feldwerte,
                _pruefbereich_checkliste: checklisteErgebnisse,
            };

            if (istBearbeiten && element?.id) {
                await elementeApi.update(element.id, {
                    element_id_kunde: elementIdKunde,
                    raum,
                    status,
                    technische_daten: technischeDaten,
                });
                onClose();
            } else {
                const angelegt = await elementeApi.create({
                    typ: bereich.schluessel,
                    stockwerk_id: stockwerkId,
                    element_id_kunde: elementIdKunde,
                    raum,
                    status,
                    technische_daten: technischeDaten,
                });
                // Nicht sofort schliessen: das neue Element hat jetzt eine
                // ID, mit der direkt Fotos ergaenzt werden koennen (Phase 6,
                // Luecke 2), statt die Erfassung dafuer erneut zu oeffnen.
                setAngelegtesElementId(angelegt?.id ?? null);
                if (!angelegt?.id) onClose();
            }
        } catch (err) {
            setFehler('Fehler beim Speichern. Bitte erneut versuchen.');
        } finally {
            setSpeichert(false);
        }
    };

    // Nach dem Anlegen: statt sofort zu schliessen, direkt Fotos anbieten
    // (Phase 6, Luecke 2) -- FotoPanel braucht dafuer eine elementId, die
    // vor dem ersten Speichern noch nicht existiert.
    if (angelegtesElementId) {
        return (
            <div className="fixed inset-0 bg-white z-[60] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5">
                <div className="bg-secondary-50 p-6 border-b flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-2xl font-black text-secondary-900">{bereich.bezeichnung} gespeichert</h2>
                        <p className="text-secondary-500 font-bold uppercase tracking-widest text-[10px] mt-1">
                            {elementIdKunde} · {stockwerkName}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-4 bg-white shadow-sm border border-secondary-200 rounded-3xl hover:bg-rose-50 hover:text-rose-600 transition-all active:scale-[0.9]"
                    >
                        <X size={24} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 md:p-12">
                    <div className="max-w-2xl mx-auto space-y-4">
                        <p className="text-secondary-500 font-medium">Jetzt können Fotos zu diesem Element ergänzt werden.</p>
                        <FotoPanel elementId={angelegtesElementId} />
                    </div>
                </div>
                <div className="p-6 bg-white border-t flex gap-4 shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 py-6 bg-primary-600 text-white rounded-3xl font-black text-xl hover:scale-[1.02] active:scale-[0.95] transition-all"
                    >
                        Fertig
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-white z-[60] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5">
            {/* Header */}
            <div className="bg-secondary-50 p-6 border-b flex justify-between items-center shrink-0">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-black text-secondary-900">
                            {bereich.bezeichnung} — {istBearbeiten ? 'Bearbeiten' : 'Erfassung'}
                        </h2>
                        <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-lg text-sm font-black tracking-tight">
                            {bereich.schluessel}
                        </span>
                    </div>
                    <p className="text-secondary-500 font-bold uppercase tracking-widest text-[10px] mt-1">
                        {stockwerkName}
                        {bereich.norm_bezug ? ` · ${bereich.norm_bezug}` : ''}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="p-4 bg-white shadow-sm border border-secondary-200 rounded-3xl hover:bg-rose-50 hover:text-rose-600 transition-all active:scale-[0.9]"
                >
                    <X size={24} />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-12">
                <div className="max-w-2xl mx-auto space-y-12">
                    {/* Identifikation */}
                    <div className="space-y-8">
                        <div className="space-y-2">
                            <h3 className="text-3xl font-black text-secondary-900 tracking-tight">Identifikation</h3>
                            <p className="text-secondary-500 font-medium">Wo befindet sich das Element?</p>
                        </div>
                        <div className="grid grid-cols-1 gap-6">
                            <div className="space-y-3">
                                <label className="text-sm font-black uppercase tracking-widest text-secondary-400">
                                    Bezeichnung / Inventarnummer *
                                </label>
                                <input
                                    className="w-full p-5 bg-secondary-50 border-4 border-secondary-100 rounded-[2rem] text-xl font-black focus:border-primary-500 focus:bg-white transition-all outline-none"
                                    placeholder="z.B. FL-EG-01"
                                    value={elementIdKunde}
                                    onChange={(e) => setElementIdKunde(e.target.value)}
                                />
                            </div>
                            <div className="space-y-3">
                                <label className="text-sm font-black uppercase tracking-widest text-secondary-400">
                                    Raum / Standort
                                </label>
                                <input
                                    className="w-full p-5 bg-secondary-50 border-4 border-secondary-100 rounded-[2rem] text-xl font-black focus:border-primary-500 focus:bg-white transition-all outline-none"
                                    placeholder="z.B. Flur EG"
                                    value={raum}
                                    onChange={(e) => setRaum(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Fotos -- nur im Bearbeiten-Modus: vor dem ersten
                        Speichern existiert noch keine elementId, an der
                        FotoPanel Aufnahmen haengen koennte. */}
                    {istBearbeiten && element?.id && (
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <h3 className="text-3xl font-black text-secondary-900 tracking-tight">Fotos</h3>
                            </div>
                            <FotoPanel elementId={element.id} />
                        </div>
                    )}

                    {/* Technische Felder aus dem Katalog */}
                    {bereich.technische_felder.length > 0 && (
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <h3 className="text-3xl font-black text-secondary-900 tracking-tight">Technische Daten</h3>
                                <p className="text-secondary-500 font-medium">Angaben zum Bauteil / zur Anlage</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {bereich.technische_felder.map((feld) => (
                                    <TechnischesFeldInput
                                        key={feld.schluessel}
                                        feld={feld}
                                        wert={feldwerte[feld.schluessel]}
                                        onChange={(w) => setFeldwert(feld, w)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Checkliste */}
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <h3 className="text-3xl font-black text-secondary-900 tracking-tight">Prüfpunkte</h3>
                            <p className="text-secondary-500 font-medium">Checkliste dieses Prüfbereichs</p>
                        </div>

                        {/* Hinweise aus der Wissensdatenbank zu genau diesem
                            Pruefbereich -- gerade hier, waehrend der
                            eigentlichen Vor-Ort-Pruefung, ist der Zugriff am
                            wertvollsten. */}
                        <WissensPanel art="PRUEFBEREICH" ziel={bereich.schluessel} />

                        {ladeChecklisten ? (
                            <div className="flex items-center gap-3 text-secondary-400 p-8">
                                <Loader2 className="animate-spin" size={20} />
                                Checkliste wird geladen…
                            </div>
                        ) : checklisten.length === 0 ? (
                            <div className="p-8 bg-secondary-50 rounded-[2rem] border-2 border-dashed border-secondary-200 text-secondary-500 font-medium flex items-center gap-3">
                                <HelpCircle size={20} />
                                Für diesen Prüfbereich ist noch keine Checkliste hinterlegt.
                            </div>
                        ) : (
                            checklisten.map((cl) => (
                                <div key={cl.id} className="p-6 bg-secondary-50 rounded-[2rem] border-4 border-secondary-100 space-y-4">
                                    <div className="flex items-baseline justify-between gap-4">
                                        <h4 className="text-lg font-black text-secondary-900">{cl.bezeichnung}</h4>
                                        {cl.din_norm && (
                                            <span className="text-[10px] font-black uppercase tracking-widest text-secondary-400">
                                                {cl.din_norm}
                                            </span>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        {cl.pruefpunkte.map((p) => (
                                            <div key={p.id} className="p-4 bg-white rounded-2xl border-2 border-secondary-100 space-y-3">
                                                <div className="flex items-start justify-between gap-3">
                                                    <span className="font-bold text-secondary-900 text-sm">
                                                        {p.text}
                                                        {p.pflicht && <span className="text-rose-500"> *</span>}
                                                    </span>
                                                </div>
                                                {p.grenzwert && (
                                                    <div className="text-xs text-secondary-500 font-medium">
                                                        Grenzwert: <span className="font-bold text-secondary-700">{p.grenzwert}</span>
                                                    </div>
                                                )}
                                                {p.quelle && (
                                                    <div className="text-[10px] text-secondary-400">Quelle: {p.quelle}</div>
                                                )}
                                                <div className="flex gap-2">
                                                    {(['i.O.', 'n.i.O.', 'nicht_geprueft'] as Bewertung[]).map((b) => (
                                                        <button
                                                            key={b}
                                                            onClick={() => setBewertung(p.id, b)}
                                                            className={`flex-1 py-2.5 rounded-xl font-black text-xs border-4 transition-all ${
                                                                (bewertungen[p.id] ?? 'nicht_geprueft') === b
                                                                    ? b === 'i.O.'
                                                                        ? 'bg-emerald-600 border-emerald-600 text-white'
                                                                        : b === 'n.i.O.'
                                                                          ? 'bg-rose-600 border-rose-600 text-white'
                                                                          : 'bg-secondary-400 border-secondary-400 text-white'
                                                                    : 'bg-white border-secondary-200 text-secondary-400'
                                                            }`}
                                                        >
                                                            {b === 'nicht_geprueft' ? 'n. geprüft' : b}
                                                        </button>
                                                    ))}
                                                </div>
                                                <input
                                                    className="w-full p-2.5 bg-secondary-50 border-2 border-secondary-100 rounded-xl text-xs font-medium outline-none focus:border-primary-400"
                                                    placeholder="Notiz (optional)"
                                                    value={notizen[p.id] || ''}
                                                    onChange={(e) => setNotizen((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {(fehlendePflichtfelder.length > 0 || fehlendePflichtpunkte.length > 0) && (
                        <div className="p-5 bg-amber-50 border-2 border-amber-100 rounded-2xl flex items-start gap-3 text-amber-700 text-sm font-bold">
                            <AlertTriangle size={20} className="shrink-0 mt-0.5" />
                            <span>
                                Hinweis: {fehlendePflichtfelder.length > 0 && `${fehlendePflichtfelder.length} Pflichtfeld(er) `}
                                {fehlendePflichtfelder.length > 0 && fehlendePflichtpunkte.length > 0 && 'und '}
                                {fehlendePflichtpunkte.length > 0 && `${fehlendePflichtpunkte.length} Pflicht-Prüfpunkt(e) `}
                                noch offen. Speichern ist trotzdem möglich.
                            </span>
                        </div>
                    )}

                    {fehler && (
                        <div className="p-5 bg-rose-50 border-2 border-rose-100 rounded-2xl text-rose-700 text-sm font-bold">{fehler}</div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="p-6 bg-white border-t flex gap-4 shrink-0">
                <button
                    onClick={onClose}
                    className="flex-1 py-6 bg-secondary-100 text-secondary-700 rounded-3xl font-black text-xl hover:bg-secondary-200 active:scale-[0.95] transition-all"
                >
                    Abbrechen
                </button>
                <button
                    onClick={handleSave}
                    disabled={!kannSpeichern}
                    className="flex-[2] py-6 bg-primary-600 text-white rounded-3xl font-black text-xl flex items-center justify-center gap-2 shadow-xl shadow-primary-100 hover:scale-[1.02] active:scale-[0.95] transition-all disabled:opacity-50"
                >
                    {speichert ? <Loader2 className="animate-spin" size={24} /> : <Save size={24} />}
                    {speichert ? 'Speichert…' : 'Speichern'}
                </button>
            </div>
        </div>
    );
}

function TechnischesFeldInput({
    feld,
    wert,
    onChange,
}: {
    feld: TechnischesFeld;
    wert: any;
    onChange: (wert: any) => void;
}) {
    const label = (
        <label className="text-xs font-black uppercase tracking-widest text-secondary-400 ml-2">
            {feld.bezeichnung}
            {feld.einheit ? ` (${feld.einheit})` : ''}
            {feld.pflicht && <span className="text-rose-500"> *</span>}
        </label>
    );

    if (feld.typ === 'JA_NEIN') {
        return (
            <div className="space-y-2">
                {label}
                <div className="flex gap-2">
                    {[
                        { v: true, t: 'Ja' },
                        { v: false, t: 'Nein' },
                    ].map((opt) => (
                        <button
                            key={String(opt.v)}
                            onClick={() => onChange(opt.v)}
                            className={`flex-1 py-3 rounded-xl font-black text-sm border-4 transition-all ${
                                wert === opt.v ? 'bg-primary-600 border-primary-600 text-white' : 'bg-secondary-50 border-secondary-100 text-secondary-600'
                            }`}
                        >
                            {opt.t}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    if (feld.typ === 'AUSWAHL' && feld.optionen && feld.optionen.length > 0) {
        return (
            <div className="space-y-2">
                {label}
                <select
                    className="w-full p-4 bg-secondary-50 border-4 border-secondary-100 rounded-2xl font-bold outline-none focus:border-primary-500"
                    value={wert ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="">— auswählen —</option>
                    {feld.optionen.map((o) => (
                        <option key={o} value={o}>
                            {o}
                        </option>
                    ))}
                </select>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {label}
            <input
                type={feld.typ === 'ZAHL' ? 'number' : feld.typ === 'DATUM' ? 'date' : 'text'}
                className="w-full p-4 bg-secondary-50 border-4 border-secondary-100 rounded-2xl font-bold outline-none focus:border-primary-500"
                value={wert ?? ''}
                onChange={(e) => onChange(feld.typ === 'ZAHL' ? e.target.value.replace(/[^0-9.,-]/g, '') : e.target.value)}
            />
        </div>
    );
}
